import { useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useStudio } from "@/context/StudioContext";
import { HERO_IMAGE, SHOWREEL } from "@/i18n/content";

const YT_ID = "bJ-2Q5FWFss";

export const ShowreelVideoBar = () => {
    const { lang } = useStudio();
    const wrapRef = useRef(null);
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const [ytFailed, setYtFailed] = useState(false);
    const [muted, setMuted] = useState(true);
    const [playing, setPlaying] = useState(true);

    useEffect(() => {
        let cancelled = false;
        let watchdog;

        const mount = () => {
            if (cancelled || playerRef.current || !wrapRef.current) return;
            playerRef.current = new window.YT.Player(wrapRef.current, {
                videoId: YT_ID,
                playerVars: {
                    autoplay: 1,
                    mute: 1,
                    loop: 1,
                    playlist: YT_ID,
                    controls: 0,
                    modestbranding: 1,
                    rel: 0,
                    playsinline: 1,
                },
                events: {
                    onReady: (e) => {
                        e.target.playVideo();
                        watchdog = setTimeout(() => {
                            try {
                                if (playerRef.current && playerRef.current.getPlayerState() === -1) {
                                    playerRef.current.destroy();
                                    playerRef.current = null;
                                    setYtFailed(true);
                                }
                            } catch {
                                setYtFailed(true);
                            }
                        }, 4000);
                    },
                    onError: () => {
                        try {
                            playerRef.current?.destroy();
                        } catch {
                            /* noop */
                        }
                        playerRef.current = null;
                        if (!cancelled) setYtFailed(true);
                    },
                },
            });
        };

        if (window.YT && window.YT.Player) {
            mount();
        } else {
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (prev) prev();
                mount();
            };
            if (!document.querySelector('script[src*="iframe_api"]')) {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/iframe_api";
                document.head.appendChild(tag);
            }
        }

        return () => {
            cancelled = true;
            clearTimeout(watchdog);
        };
    }, []);

    const toggleSound = () => {
        if (!ytFailed && playerRef.current) {
            if (muted) playerRef.current.unMute();
            else playerRef.current.mute();
        } else if (videoRef.current) {
            videoRef.current.muted = !muted;
        }
        setMuted(!muted);
    };

    const togglePlay = () => {
        if (!ytFailed && playerRef.current) {
            if (playing) playerRef.current.pauseVideo();
            else playerRef.current.playVideo();
        } else if (videoRef.current) {
            if (videoRef.current.paused) videoRef.current.play();
            else videoRef.current.pause();
        }
        setPlaying(!playing);
    };

    const onVideoError = (e) => {
        const el = e.currentTarget;
        if (!el.dataset.fbk) {
            el.dataset.fbk = "1";
            el.src = SHOWREEL.video;
        } else if (el.dataset.fbk === "1") {
            el.dataset.fbk = "2";
            el.src = SHOWREEL.videoFallback;
        }
    };

    return (
        <section
            data-testid="showreel-section"
            className="relative mt-16 w-full overflow-hidden border-y border-border/50 bg-neutral-900"
        >
            <div className="relative h-[380px] w-full overflow-hidden sm:h-[440px] md:h-[520px]">
                <img
                    src="/hero-poster.webp"
                    alt=""
                    aria-hidden="true"
                    fetchpriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover brightness-[0.85] contrast-[1.05]"
                />
                {!ytFailed ? (
                    <div className="yt-cover absolute inset-0" data-testid="showreel-video">
                        <div ref={wrapRef} />
                    </div>
                ) : (
                    <video
                        ref={videoRef}
                        data-testid="showreel-video"
                        className="absolute inset-0 h-full w-full object-cover brightness-[0.85] contrast-[1.05]"
                        src="/showreel.mp4"
                        poster={HERO_IMAGE.url}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        onError={onVideoError}
                    />
                )}

                <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/50 to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />

                <div className="absolute right-6 top-6 z-20 flex items-center gap-2 sm:right-12">
                    <button
                        data-testid="showreel-sound-toggle"
                        onClick={toggleSound}
                        aria-label={muted ? SHOWREEL.soundOn[lang] : SHOWREEL.soundOff[lang]}
                        className="rounded-full border border-white/15 bg-black/40 p-2.5 text-white/85 backdrop-blur-md transition-colors duration-300 hover:border-[#EFE7D6] hover:text-[#EFE7D6]"
                    >
                        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                    </button>
                    <button
                        data-testid="showreel-play-toggle"
                        onClick={togglePlay}
                        aria-label={playing ? SHOWREEL.pauseLabel[lang] : SHOWREEL.playLabel[lang]}
                        className="rounded-full border border-white/15 bg-black/40 p-2.5 text-white/85 backdrop-blur-md transition-colors duration-300 hover:border-[#EFE7D6] hover:text-[#EFE7D6]"
                    >
                        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </button>
                </div>
            </div>
        </section>
    );
};
